import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Helmet from 'react-helmet'
import { QUICK_APPOINTMENT_PAGE } from 'constant'
import { getServiceShortenedName, getServiceAlias } from '../../helpers'
import { SERVICE_CATEGORY_CONTENT_ID, SERVICE_CATEGORY_CTA_WRAPPER_ID } from '../../constants'
import HTMLContainer from 'components/common/HTMLContainer/HTMLContainer'
import Link, { A } from 'components/common/Link/Link'
import Icon from 'components/common/Icon/Icon'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Theme from 'components/common/Theme/Theme'

const ServiceCategoryPage = React.memo(({
  categoryId,
  categoryName,
  categoryLongDescription,
  serviceUrl,
  storeBaseURL,
  storeCityAndState,
  displayFloatingCTAs,
  navigationHandler,
  handleApptRequestClick,
  appointmentBtnTitle,
  serviceSchema,
  serviceCategorySchema
}) => {
  const CTAs =
    <Fragment>
      <A
        button
        narrow
        href={ QUICK_APPOINTMENT_PAGE }
        handler={ handleApptRequestClick }
        full="mobile">
        { appointmentBtnTitle }
      </A>
      <A
        button
        narrow
        href={ serviceUrl }
        full="mobile">
        View { getServiceShortenedName(categoryId, categoryName) } Services
      </A>
    </Fragment>

  return <div>
    <Helmet>
      <script type="application/ld+json">
        {
          JSON.stringify(serviceSchema)
        }
      </script>

      <script type="application/ld+json" id="store-details-schema">
        {
          JSON.stringify(serviceCategorySchema)
        }
      </script>
    </Helmet>

    <Title>{ `${getServiceAlias(categoryId, categoryName)} Near Me` }</Title>
    <FloatingWrapper hidden={ !displayFloatingCTAs }>
      { CTAs }
    </FloatingWrapper>
    <PageWrapper id={ SERVICE_CATEGORY_CONTENT_ID } content={ categoryLongDescription } />
    <ButtonWrapper id={ SERVICE_CATEGORY_CTA_WRAPPER_ID }>
      { CTAs }
    </ButtonWrapper>
    <ServicesLink
      to={ storeBaseURL }
      prevent
      onClick={ e => navigationHandler(e) } >
      <Icon asset="arrow-left" width={ 8 } alt="" />&nbsp;
      Back to All Services
    </ServicesLink>
  </div>
})


export default ServiceCategoryPage

ServiceCategoryPage.propTypes = {
  appointmentBtnTitle: PropTypes.string,
  categoryId: PropTypes.string,
  categoryLongDescription: PropTypes.string,
  categoryName: PropTypes.string,
  displayFloatingCTAs: PropTypes.bool,
  handleApptRequestClick: PropTypes.func,
  navigationHandler: PropTypes.func,
  serviceCategorySchema: PropTypes.string,
  serviceSchema: PropTypes.string,
  serviceUrl: PropTypes.string,
  storeBaseURL: PropTypes.string,
  storeCityAndState: PropTypes.string
}

const PageWrapper = styled(HTMLContainer)`
  ${Mixin.font.GothamBook(14, 400)}
  ${Mixin.responsive('line-height', '18px', '22px')}

  h3 {
    ${Mixin.font.GothamBold(17)}
    margin-bottom: 5px;
  }

  h4 {
    ${Mixin.font.GothamBold(15)}
    margin-bottom: 5px;
  }

  .service-desc-paragraph {
    margin-top: 5px;
  }

  .service-page-section {
    margin-top: 10px;
  }

  .item-list > li {
    margin-bottom: 5px;
    list-style-type: disc;
  }

  .item-description {
    margin: 3px 0;
  }

  .numbered-list > li {
    margin-bottom: 5px;
    list-style-type: decimal;
  }

  .flex-container {
    margin-top: 15px;
    display: flex;
    flex-wrap: wrap;
  }

  .flex-container-4 > div {
    ${Mixin.responsive('width', '20%', '100%')}
  }

  .flex-container-3 > div {
    ${Mixin.responsive('width', '25%', '100%')}
  }

  .flex-container > div:not(:first-child) {
    ${Media.desktop`
      margin-left: 40px;
    `}
  }

  .two-column-list {
    columns: 2;
    width: 70%;
    ${Media.mobile`
      columns: 1;
      width: 100%;
    `}
  }

  .item-list > ul > li {
    list-style-type: circle;
  }

  .registered-mark {
    font-size: 10px;
    vertical-align: super;
  }
`

const Title = styled.h2`
  ${Mixin.font.GothamBold(19, 600)}
  text-transform: uppercase;
  transform: scale(1, 1.3);
`

const FloatingWrapper = styled.div`
  width: 100%;
  background-color: ${Theme.colors.white};
  box-shadow: 0 -2px 5px ${Theme.colors.transparent_grey_hover};
  padding: 20px 10px;
  position: fixed;
  left: 0;
  bottom: 0;
  ${Media.mobile`
    *:first-child {
      margin-bottom: 15px;
    }
  `}

  ${Media.desktop`
    display: none;
  `}
`

const ButtonWrapper = styled.div`
  ${Mixin.responsive('margin', '50px 0', '30px 0')}

  *:first-child {
    ${Media.desktop`
      margin-right: 10px;
    `}
    ${Media.mobile`
      margin-bottom: 15px;
    `}
  }
`

const ServicesLink = styled(Link)` 
  ${Mixin.font.GothamBold(13)}
`
